'use client';

import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCreationDetail } from '@/app/apis';
import { AgentLineGenerateVideo } from '@/app/components/icons/AgentLineGenerateVideo';
import { BaseFillPlay } from '@/app/components/icons/BaseFillPlay';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { ImageUploader } from '@/app/components/shared/ImageUploader';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { toast } from '@/app/components/ui/use-toast';
import {
  DEFAULT_FORM_PARAMS,
  DEFAULT_IMAGE_FORM_PARAMS,
  MAX_IMAGE_FILE_SIZE,
  MAX_PROMPT_LENGTH,
  MODEL_VERSION_SETTINGS_MAP,
  NewFeatureKeys,
  Q2_PRO_REFERENCE_VIDEO_MAX_DURATION,
  Q2_PRO_REFERENCE_VIDEO_MAX_FILE_SIZE,
  RECREATE_TASK_KEY,
  SITE,
  TASK_SETTING_OPTION,
} from '@/app/constants';
import {
  useFetch,
  useFetchRecreateData,
  useGlobalModal,
  useNewFeature,
  useResizeByResolution,
  useUpgradeDialogs,
} from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  ConfigItemOption,
  CreationWithTask,
  FormImagePrompt,
  FormParams,
  FormVideoPrompt,
  ImageUploadFile,
  MentionItem,
  ReferencePrompt,
  TaskInfo,
  TaskParams,
  TaskSettings,
  VideoUploadFile,
} from '@/app/types';
import {
  buildQuickEditToolDraftFromCreation,
  buildQuickEditToolDraftFromTask,
  buildQuickEditToolSettings,
  cn,
  convertForm,
  getAvailableOptions,
  getMaxImageAndElementCount,
  getNextAvailableImageIdx,
  getNextImageName,
  getQuickEditToolModelVersion,
  getResourceUrl,
  getTextPromptLength,
  isOptionAllowed,
  mergeCreationAndTask,
  normalizeDiscreteVideoDurationToSeconds,
  validateAndCorrectSettings,
} from '@/app/utils';
import { isHevcSupported, resizeSourceImage } from '@/app/utils/client';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { useRunTempUploadBatch } from '../../_hooks/use-run-temp-upload-batch';
import { getToolTempSourceImageItems, getToolTempSourceVideoItems } from '../../_utils/tool-temp-upload-items';
import { CreationSelectorDialog } from '../CreationSelectorDialog';
import { ExampleMediaPreview } from '../ExampleMediaPreview';
import { ToolFormContainer } from '../ToolFormContainer';
import { ToolFormSubmitButton } from '../ToolFormSubmitButton';
import { ToolMediaUpload } from '../ToolMediaUpload';
import { ToolMediaUploadHeader } from '../ToolMediaUploadHeader';
import { ToolSettingsWithConfig } from '../ToolSettingsWithConfig';
import { QUICK_EDIT_MENTION_COLOR, QuickEditTextarea } from './QuickEditTextarea';

interface QuickEditToolFormProps {
  mode: 'video' | 'image';
}

const QUICK_EDIT_VIDEO_ASPECT_RATIO_OPTIONS = ['16:9', '9:16', '1:1', '4:3', '3:4'] as const;
const QUICK_EDIT_IMAGE_ASPECT_RATIO_OPTIONS = ['intelligent', '16:9', '9:16', '1:1', '4:3', '3:4'] as const;
const QUICK_EDIT_VIDEO_DURATION_OPTIONS = ['auto', '2', '3', '4', '5', '6', '7', '8'] as const;
const QUICK_EDIT_VIDEO_RESOLUTION_OPTIONS = ['1080p'] as const;
const QUICK_EDIT_IMAGE_RESOLUTION_OPTIONS = ['1080p', '2k', '4k'] as const;

const parseAspectRatioValue = (aspectRatio: (typeof QUICK_EDIT_VIDEO_ASPECT_RATIO_OPTIONS)[number]): number => {
  const [width, height] = aspectRatio.split(':').map(Number);
  return width / height;
};

const getClosestAspectRatio = (
  aspectRatio?: number,
): (typeof QUICK_EDIT_VIDEO_ASPECT_RATIO_OPTIONS)[number] | undefined => {
  if (!aspectRatio || !Number.isFinite(aspectRatio)) return undefined;

  return QUICK_EDIT_VIDEO_ASPECT_RATIO_OPTIONS.reduce<
    (typeof QUICK_EDIT_VIDEO_ASPECT_RATIO_OPTIONS)[number] | undefined
  >((closestRatio, currentRatio) => {
    if (!closestRatio) return currentRatio;

    const currentDistance = Math.abs(parseAspectRatioValue(currentRatio) - aspectRatio);
    const closestDistance = Math.abs(parseAspectRatioValue(closestRatio) - aspectRatio);

    return currentDistance < closestDistance ? currentRatio : closestRatio;
  }, undefined);
};

export const QuickEditToolForm: FC<QuickEditToolFormProps> = ({ mode }) => {
  const t = useTranslations();
  const taskT = useTranslations('task');
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const storeApi = useStoreApi();
  const searchParams = useSearchParams();
  const formTaskType = mode === 'video' ? 'character2video' : 'reference2image';
  const submitTaskType = (mode === 'video' ? 'video_edit' : 'image_edit') as TaskParams['type'];
  const isVideoType = mode === 'video';
  const creationId = searchParams.get('creationId') || '';
  const recreateTaskId = searchParams.get(RECREATE_TASK_KEY) || '';

  const containerRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [openMediaSelector, setOpenMediaSelector] = useState(false);
  const [selectedCreationIndex, setSelectedCreationIndex] = useState<number | null>(null);
  const [isSourceMediaUploading, setIsSourceMediaUploading] = useState(false);

  // 拖拽多图时的路由跟踪：
  // - isSourceClaimedRef: 同步标记源图槽是否已被本批次占用（避免 React state 异步更新导致的竞态）
  // - claimedForSourceSet: 记录哪些 fileId 已被路由到 source，保证同一文件多次 onChange 回调路由一致
  const isSourceClaimedRef = useRef(false);
  const claimedForSourceSet = useRef(new Set<string>());
  // 仅当前一批符合规则的拖拽时，把原素材 mention 放到最前面一次。
  const shouldPrependNextSourceMentionRef = useRef(false);

  const { open: openSimpleCreationDetailModal } = useGlobalModal('simpleCreationDetailModal');

  const { userInfo, quickEditDraft, replaceQuickEditToolDraft, updateQuickEditToolDraft } = useStore((state) => ({
    userInfo: state.userInfo,
    quickEditDraft: mode === 'video' ? state.videoEditToolDraft : state.imageEditToolDraft,
    replaceQuickEditToolDraft: state.replaceQuickEditToolDraft,
    updateQuickEditToolDraft: state.updateQuickEditToolDraft,
  }));
  const { source, sourceCreationId, sourceVideo, sourceImage, textPrompt, referencePrompts, settings } = quickEditDraft;
  const modelVersion = getQuickEditToolModelVersion(mode);
  const settingsKey = useMemo(() => MODEL_VERSION_SETTINGS_MAP[modelVersion], [modelVersion]);
  const originalMediaId = useMemo(() => `original-image-${source.uri || 'uploaded'}`, [source.uri]);
  const originalReferencePrompt = useMemo(() => {
    return referencePrompts.find((prompt) => prompt.id === originalMediaId) || null;
  }, [originalMediaId, referencePrompts]);
  const currentCoverImageUrl = useMemo(() => {
    if (originalReferencePrompt?.type === 'video') {
      return originalReferencePrompt.prompt?.coverImagePreview || originalReferencePrompt.prompt?.coverImage || '';
    }

    return sourceVideo?.cover_url || sourceVideo?.cover_preview_url || '';
  }, [originalReferencePrompt, sourceVideo?.cover_preview_url, sourceVideo?.cover_url]);

  const getOriginalMention = useCallback(
    (name: string) => ({
      type: 'mention' as const,
      mention: {
        id: originalMediaId,
        name,
        color: QUICK_EDIT_MENTION_COLOR,
      },
    }),
    [originalMediaId],
  );

  const consumeShouldPrependNextSourceMention = useCallback(() => {
    const shouldPrepend = shouldPrependNextSourceMentionRef.current;
    shouldPrependNextSourceMentionRef.current = false;
    return shouldPrepend;
  }, []);

  useRunTempUploadBatch(
    isVideoType ? userInfo : null,
    'Failed to upload temp source video',
    (refs) =>
      getToolTempSourceVideoItems(refs, {
        sourceVideo: isVideoType ? sourceVideo : null,
        getSourceVideo: () =>
          storeApi.getState()[mode === 'video' ? 'videoEditToolDraft' : 'imageEditToolDraft'].sourceVideo,
        setSourceVideo: (video) =>
          updateQuickEditToolDraft(mode, (draft) => ({
            ...draft,
            sourceVideo: video,
            referencePrompts: draft.referencePrompts.map((prompt) =>
              prompt.id === originalMediaId && prompt.type === 'video' && video
                ? {
                    ...prompt,
                    status: 'done',
                    prompt: {
                      video: video.src,
                      videoPreview: video.preview || video.src,
                      coverImage: video.cover_url || video.cover_preview_url,
                      coverImagePreview: video.cover_preview_url || video.cover_url || video.preview,
                    },
                  }
                : prompt,
            ),
          })),
        onStatusChange: (status) => {
          setIsSourceMediaUploading(status === 'uploading');
        },
      }),
    [isVideoType, mode, originalMediaId, sourceVideo, updateQuickEditToolDraft, userInfo],
  );

  useRunTempUploadBatch(
    !isVideoType ? userInfo : null,
    'Failed to upload temp source image',
    (refs) =>
      getToolTempSourceImageItems(refs, {
        imagePrompt: !isVideoType ? sourceImage : null,
        getImagePrompt: () =>
          storeApi.getState()[mode === 'video' ? 'videoEditToolDraft' : 'imageEditToolDraft'].sourceImage,
        setImagePrompt: (imagePrompt) =>
          updateQuickEditToolDraft(mode, (draft) => ({
            ...draft,
            sourceImage: imagePrompt,
            referencePrompts: draft.referencePrompts.map((prompt) =>
              prompt.id === originalMediaId && prompt.type === 'image' && imagePrompt
                ? {
                    ...prompt,
                    status: 'done',
                    prompt: {
                      sourceImage: imagePrompt.selectedImage,
                      sourceImagePreview: imagePrompt.selectedImagePreview,
                      selectedImage: imagePrompt.selectedImage,
                      selectedImagePreview: imagePrompt.selectedImagePreview,
                    },
                  }
                : prompt,
            ),
          })),
        onStatusChange: (status) => {
          setIsSourceMediaUploading(status === 'uploading');
        },
      }),
    [isVideoType, mode, originalMediaId, sourceImage, updateQuickEditToolDraft, userInfo],
  );

  const maxImageCount = useMemo(() => {
    return (
      getMaxImageAndElementCount(modelVersion) -
      referencePrompts.filter((prompt) => prompt.type === 'image' || prompt.type === 'element').length
    );
  }, [modelVersion, referencePrompts]);
  const maxReferenceImageCount = useMemo(() => {
    return getMaxImageAndElementCount(modelVersion);
  }, [modelVersion]);

  const getSettingsFromSourceVideo = useCallback(
    (video: { duration?: number; aspectRatio?: number }) => {
      if (!isVideoType) return undefined;

      const nextAspectRatio = getClosestAspectRatio(video.aspectRatio) || settings.aspectRatio;
      const nextSettings = buildQuickEditToolSettings({
        mode,
        userPlan: userInfo?.subs_plan,
        defaultAspectRatio: nextAspectRatio,
        defaultDuration: normalizeDiscreteVideoDurationToSeconds(video.duration),
      });

      return {
        aspectRatio: nextSettings.aspectRatio,
        duration: nextSettings.duration,
      };
    },
    [isVideoType, mode, settings.aspectRatio, userInfo?.subs_plan],
  );

  const handleUploadVideo = useCallback(
    (uploadFile: VideoUploadFile, options?: { sourceCreationId?: string }) => {
      if (uploadFile.status === 'uploading') {
        setIsSourceMediaUploading(true);
        const shouldPrependOriginalMention = consumeShouldPrependNextSourceMention();

        updateQuickEditToolDraft(mode, (draft) => {
          const hasOriginalMention = draft.textPrompt.some(
            (item) => item.type === 'mention' && item.mention.id === originalMediaId,
          );
          const originalVideoReference = {
            type: 'video',
            id: originalMediaId,
            name: t('create.createForm.originalVideoName'),
            status: 'uploading',
            prompt: null,
          } as ReferencePrompt;

          return {
            ...draft,
            referencePrompts: shouldPrependOriginalMention
              ? [originalVideoReference, ...draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId)]
              : [...draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId), originalVideoReference],
            textPrompt: hasOriginalMention
              ? draft.textPrompt
              : shouldPrependOriginalMention
                ? [getOriginalMention(t('create.createForm.originalVideoName')), ...draft.textPrompt]
                : [...draft.textPrompt, getOriginalMention(t('create.createForm.originalVideoName'))],
          };
        });
        return;
      }

      if (uploadFile.status === 'done' && uploadFile.file) {
        setIsSourceMediaUploading(false);
        const file = uploadFile.file;
        const nextSourceSettings = getSettingsFromSourceVideo(file);
        const videoUri = file.src || file.preview;
        const videoPreview = file.preview || file.src;
        const coverImage = file.cover_url || file.cover_preview_url;
        const coverImagePreview = file.cover_preview_url || file.cover_url || videoPreview;

        updateQuickEditToolDraft(mode, (draft) => {
          const hasOriginalMention = draft.textPrompt.some(
            (item) => item.type === 'mention' && item.mention.id === originalMediaId,
          );

          return {
            ...draft,
            sourceCreationId: options?.sourceCreationId,
            sourceVideo: file,
            referencePrompts: [
              ...draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId),
              {
                type: 'video',
                id: originalMediaId,
                name: t('create.createForm.originalVideoName'),
                status: 'done',
                prompt: {
                  video: videoUri,
                  videoPreview,
                  coverImage,
                  coverImagePreview,
                },
              },
            ],
            textPrompt: hasOriginalMention
              ? draft.textPrompt
              : [
                  ...draft.textPrompt,
                  {
                    type: 'mention',
                    mention: {
                      id: originalMediaId,
                      name: t('create.createForm.originalVideoName'),
                      color: QUICK_EDIT_MENTION_COLOR,
                    },
                  },
                ],
            settings: {
              ...draft.settings,
              ...(nextSourceSettings?.aspectRatio ? { aspectRatio: nextSourceSettings.aspectRatio } : {}),
              ...(nextSourceSettings?.duration !== undefined ? { duration: nextSourceSettings.duration } : {}),
            },
          };
        });
        return;
      }

      if (uploadFile.status === 'removed' || uploadFile.status === 'error') {
        setIsSourceMediaUploading(false);
        updateQuickEditToolDraft(mode, (draft) => {
          if (draft.sourceVideo) {
            const hasOriginalMention = draft.textPrompt.some(
              (item) => item.type === 'mention' && item.mention.id === originalMediaId,
            );

            return {
              ...draft,
              referencePrompts: [
                ...draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId),
                {
                  type: 'video',
                  id: originalMediaId,
                  name: t('create.createForm.originalVideoName'),
                  status: 'done',
                  prompt: {
                    video: draft.sourceVideo.src || draft.sourceVideo.preview || '',
                    videoPreview: draft.sourceVideo.preview || draft.sourceVideo.src || '',
                    coverImage: draft.sourceVideo.cover_url || draft.sourceVideo.cover_preview_url,
                    coverImagePreview:
                      draft.sourceVideo.cover_preview_url ||
                      draft.sourceVideo.cover_url ||
                      draft.sourceVideo.preview ||
                      draft.sourceVideo.src,
                  },
                } as ReferencePrompt,
              ],
              textPrompt: hasOriginalMention
                ? draft.textPrompt
                : [...draft.textPrompt, getOriginalMention(t('create.createForm.originalVideoName'))],
            };
          }

          return {
            ...draft,
            sourceCreationId: undefined,
            referencePrompts: draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId),
            textPrompt: draft.textPrompt.filter(
              (item) => !(item.type === 'mention' && item.mention.id === originalMediaId),
            ),
          };
        });
      }
    },
    [
      consumeShouldPrependNextSourceMention,
      getOriginalMention,
      getSettingsFromSourceVideo,
      mode,
      originalMediaId,
      t,
      updateQuickEditToolDraft,
    ],
  );

  const handleUploadSourceImage = useCallback(
    (uploadFile: ImageUploadFile, options?: { sourceCreationId?: string }) => {
      if (uploadFile.status === 'uploading') {
        setIsSourceMediaUploading(true);
        const shouldPrependOriginalMention = consumeShouldPrependNextSourceMention();

        updateQuickEditToolDraft(mode, (draft) => {
          const hasOriginalMention = draft.textPrompt.some(
            (item) => item.type === 'mention' && item.mention.id === originalMediaId,
          );
          const originalImageReference = {
            type: 'image',
            id: originalMediaId,
            name: t('create.createForm.originalImageName'),
            status: 'uploading',
            prompt: uploadFile.file,
          } as ReferencePrompt;

          return {
            ...draft,
            referencePrompts: shouldPrependOriginalMention
              ? [originalImageReference, ...draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId)]
              : [...draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId), originalImageReference],
            textPrompt: hasOriginalMention
              ? draft.textPrompt
              : shouldPrependOriginalMention
                ? [getOriginalMention(t('create.createForm.originalImageName')), ...draft.textPrompt]
                : [...draft.textPrompt, getOriginalMention(t('create.createForm.originalImageName'))],
          };
        });
        return;
      }

      if (uploadFile.status === 'done' && uploadFile.file?.selectedImage) {
        setIsSourceMediaUploading(false);
        const file = uploadFile.file;

        updateQuickEditToolDraft(mode, (draft) => {
          const hasOriginalMention = draft.textPrompt.some(
            (item) => item.type === 'mention' && item.mention.id === originalMediaId,
          );

          return {
            ...draft,
            sourceCreationId: options?.sourceCreationId,
            sourceImage: file,
            referencePrompts: [
              ...draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId),
              {
                type: 'image',
                id: originalMediaId,
                name: t('create.createForm.originalImageName'),
                status: 'done',
                prompt: {
                  sourceImage: file.selectedImage,
                  sourceImagePreview: file.selectedImagePreview,
                  selectedImage: file.selectedImage,
                  selectedImagePreview: file.selectedImagePreview,
                },
              },
            ],
            textPrompt: hasOriginalMention
              ? draft.textPrompt
              : [
                  ...draft.textPrompt,
                  {
                    type: 'mention',
                    mention: {
                      id: originalMediaId,
                      name: t('create.createForm.originalImageName'),
                      color: QUICK_EDIT_MENTION_COLOR,
                    },
                  },
                ],
          };
        });
        return;
      }

      if (uploadFile.status === 'removed' || uploadFile.status === 'error') {
        setIsSourceMediaUploading(false);
        updateQuickEditToolDraft(mode, (draft) => {
          if (draft.sourceImage?.selectedImage) {
            const hasOriginalMention = draft.textPrompt.some(
              (item) => item.type === 'mention' && item.mention.id === originalMediaId,
            );

            return {
              ...draft,
              referencePrompts: [
                ...draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId),
                {
                  type: 'image',
                  id: originalMediaId,
                  name: t('create.createForm.originalImageName'),
                  status: 'done',
                  prompt: {
                    sourceImage: draft.sourceImage.selectedImage,
                    sourceImagePreview: draft.sourceImage.selectedImagePreview,
                    selectedImage: draft.sourceImage.selectedImage,
                    selectedImagePreview: draft.sourceImage.selectedImagePreview,
                  },
                } as ReferencePrompt,
              ],
              textPrompt: hasOriginalMention
                ? draft.textPrompt
                : [...draft.textPrompt, getOriginalMention(t('create.createForm.originalImageName'))],
            };
          }

          return {
            ...draft,
            sourceCreationId: undefined,
            referencePrompts: draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId),
            textPrompt: draft.textPrompt.filter(
              (item) => !(item.type === 'mention' && item.mention.id === originalMediaId),
            ),
          };
        });
      }
    },
    [consumeShouldPrependNextSourceMention, getOriginalMention, mode, originalMediaId, t, updateQuickEditToolDraft],
  );

  const handleSelectMedia = useCallback(
    (index: number | null, creation?: CreationWithTask) => {
      if (!creation) {
        setSelectedCreationIndex(null);
        return;
      }
      setSelectedCreationIndex(index);

      if (isVideoType) {
        const previewVideoUrl = getResourceUrl({
          uri: creation.uri || '',
          noMarkUri: creation.nomark_uri || '',
        });
        handleUploadVideo(
          {
            id: creation.id,
            status: 'done',
            file: {
              src: creation.uri || '',
              preview: previewVideoUrl,
              aspectRatio:
                creation.resolution?.width && creation.resolution?.height
                  ? creation.resolution.width / creation.resolution.height
                  : undefined,
              duration: creation.duration,
              cover_url: creation.cover_uri,
              cover_preview_url: creation.cover_uri,
            },
          } as VideoUploadFile,
          {
            sourceCreationId: creation.id,
          },
        );
      } else {
        const previewImageUrl = creation.nomark_uri || creation.uri || '';
        handleUploadSourceImage(
          {
            id: creation.id,
            status: 'done',
            file: {
              selectedImage: creation.uri || '',
              selectedImagePreview: previewImageUrl,
              sourceImage: creation.uri || '',
              sourceImagePreview: previewImageUrl,
              sourceImageWidth: creation.resolution?.width,
              sourceImageHeight: creation.resolution?.height,
              selectedImageWidth: creation.resolution?.width,
              selectedImageHeight: creation.resolution?.height,
            },
          } as ImageUploadFile,
          {
            sourceCreationId: creation.id,
          },
        );
      }
      setOpenMediaSelector(false);
    },
    [handleUploadSourceImage, handleUploadVideo, isVideoType],
  );

  const defaultFormParamsForTask = useMemo(() => {
    return formTaskType === 'reference2image' ? DEFAULT_IMAGE_FORM_PARAMS : DEFAULT_FORM_PARAMS;
  }, [formTaskType]);
  const { handleOpenPlanDialog } = useUpgradeDialogs();

  const validateQuickEditSettings = useCallback(
    (currentSettings: typeof settings) => {
      const normalizedSettings = validateAndCorrectSettings({
        version: modelVersion,
        type: isVideoType ? 'character2video' : 'reference2image',
        newSettings: {
          aspect_ratio: currentSettings.aspectRatio,
          resolution: currentSettings.resolution,
          ...(isVideoType && currentSettings.duration !== undefined ? { duration: currentSettings.duration } : {}),
        },
      });

      const nextSettings = {
        aspectRatio: normalizedSettings.aspect_ratio ?? currentSettings.aspectRatio,
        resolution: normalizedSettings.resolution ?? currentSettings.resolution,
        duration:
          isVideoType && currentSettings.duration !== undefined
            ? (normalizedSettings.duration ?? currentSettings.duration)
            : currentSettings.duration,
      };

      if (isVideoType && nextSettings.duration !== undefined) {
        const durationValue = nextSettings.duration === -1 ? 'auto' : String(nextSettings.duration);

        if (!isOptionAllowed('duration', durationValue, userInfo?.subs_plan, modelVersion)) {
          nextSettings.duration = (defaultFormParamsForTask[settingsKey] as { duration?: number }).duration;
        }
      }

      return nextSettings;
    },
    [defaultFormParamsForTask, isVideoType, modelVersion, settingsKey, userInfo?.subs_plan],
  );
  const validatedSettings = useMemo(
    () => validateQuickEditSettings(settings),
    [settings.aspectRatio, settings.duration, settings.resolution, validateQuickEditSettings],
  );

  const handleTaskSuccess = useCallback(
    (task: TaskInfo) => {
      const draft = buildQuickEditToolDraftFromTask({
        mode,
        task,
        taskId: task.id || recreateTaskId,
        locale,
        userPlan: userInfo?.subs_plan,
        originalVideoName: t('create.createForm.originalVideoName'),
        originalImageName: t('create.createForm.originalImageName'),
      });

      if (draft) {
        replaceQuickEditToolDraft(mode, draft);
      }
    },
    [locale, mode, recreateTaskId, replaceQuickEditToolDraft, t, userInfo?.subs_plan],
  );

  useFetchRecreateData({
    onTaskSuccess: handleTaskSuccess,
  });

  const quickEditAspectRatioOptions = useMemo<ConfigItemOption[]>(() => {
    const options = isVideoType ? QUICK_EDIT_VIDEO_ASPECT_RATIO_OPTIONS : QUICK_EDIT_IMAGE_ASPECT_RATIO_OPTIONS;

    return options.map((value) => ({
      value,
      label: value === 'intelligent' ? t('create.createForm.settings.aspectRatio.optionIntelligent.text') : value,
      showVip: false,
    }));
  }, [isVideoType, t]);

  const quickEditDurationOptions = useMemo<ConfigItemOption[]>(() => {
    if (!isVideoType) {
      return [];
    }

    const options = getAvailableOptions(modelVersion, 'character2video').duration || QUICK_EDIT_VIDEO_DURATION_OPTIONS;

    return options.map((value) => ({
      value,
      label: value === 'auto' ? t('create.createForm.settings.duration.option.auto') : value,
      showVip: !isOptionAllowed('duration', value, 'free', modelVersion),
    }));
  }, [isVideoType, modelVersion, t]);

  const quickEditResolutionOptions = useMemo<ConfigItemOption[]>(() => {
    const options = isVideoType ? QUICK_EDIT_VIDEO_RESOLUTION_OPTIONS : QUICK_EDIT_IMAGE_RESOLUTION_OPTIONS;

    return options.map((value) => ({
      value,
      label: value.toUpperCase(),
      showVip: false,
    }));
  }, [isVideoType]);
  const quickEditIncludedOptions = useMemo(() => {
    const baseOptions = isVideoType
      ? [TASK_SETTING_OPTION.AspectRatio, TASK_SETTING_OPTION.Duration, TASK_SETTING_OPTION.Resolution]
      : [TASK_SETTING_OPTION.AspectRatio, TASK_SETTING_OPTION.Resolution];

    return quickEditResolutionOptions.length > 1
      ? baseOptions
      : baseOptions.filter((option) => option !== TASK_SETTING_OPTION.Resolution);
  }, [isVideoType, quickEditResolutionOptions.length]);

  const handleQuickEditSettingsChange = useCallback(
    (newValues: Partial<typeof validatedSettings>) => {
      updateQuickEditToolDraft(mode, (draft) => ({
        ...draft,
        settings: validateQuickEditSettings({
          ...draft.settings,
          ...(newValues.aspectRatio ? { aspectRatio: newValues.aspectRatio } : {}),
          ...(newValues.resolution ? { resolution: newValues.resolution } : {}),
          ...(newValues.duration !== undefined ? { duration: newValues.duration } : {}),
        }),
      }));
    },
    [mode, updateQuickEditToolDraft, validateQuickEditSettings],
  );

  useEffect(() => {
    if (
      validatedSettings.aspectRatio === settings.aspectRatio &&
      validatedSettings.resolution === settings.resolution &&
      validatedSettings.duration === settings.duration
    ) {
      return;
    }

    updateQuickEditToolDraft(mode, (draft) => ({
      ...draft,
      settings: validatedSettings,
    }));
  }, [mode, settings.aspectRatio, settings.duration, settings.resolution, updateQuickEditToolDraft, validatedSettings]);

  useEffect(() => {
    const expectedKey = recreateTaskId ? `task:${recreateTaskId}` : creationId ? `creation:${creationId}` : undefined;
    if (!expectedKey) return;
    if (quickEditDraft.initializedKey !== expectedKey) return;

    router.replace(pathname);
  }, [creationId, pathname, quickEditDraft.initializedKey, recreateTaskId, router]);

  useFetch(
    () =>
      getCreationDetail({
        path: {
          id: creationId,
        },
      }),
    {
      ready: !!creationId && !recreateTaskId,
      refreshDeps: [creationId, recreateTaskId],
      onSuccess: ({ data, error }) => {
        if (error || !data) return;

        const mergedCreation = mergeCreationAndTask({
          creation: data.creation,
          task: data.task,
          creationId: data.creation?.id,
          extendSourceCreations: data.extend_source_creations,
        });

        replaceQuickEditToolDraft(
          mode,
          buildQuickEditToolDraftFromCreation({
            mode,
            creation: mergedCreation,
            originalMediaName: t(
              isVideoType ? 'create.createForm.originalVideoName' : 'create.createForm.originalImageName',
            ),
            userPlan: userInfo?.subs_plan,
            defaultAspectRatio: data.task?.settings?.aspect_ratio,
          }),
        );
      },
    },
  );

  const { isNewFeature, markAsClicked } = useNewFeature();
  const isQuickEditAddImageNew = useMemo(() => isNewFeature(NewFeatureKeys.QUICK_EDIT_ADD_IMAGE), [isNewFeature]);

  const { maxImageSize, imageProcessing } = useResizeByResolution(formTaskType);

  const submitValue = useMemo(() => {
    const formParams: FormParams = {
      ...defaultFormParamsForTask,
      modelVersion,
      textPromptWithReference: textPrompt,
      referencePrompts,
      isProMode: false,
      [settingsKey]: {
        ...(defaultFormParamsForTask[settingsKey] as Record<string, unknown>),
        aspect_ratio: validatedSettings.aspectRatio,
        resolution: validatedSettings.resolution,
        ...(isVideoType && validatedSettings.duration !== undefined ? { duration: validatedSettings.duration } : {}),
      },
    };
    const submitValue = convertForm({ taskType: formTaskType, formParams, modelVersion });

    if (sourceCreationId && submitValue.input.prompts) {
      let hasAttachedSourceReference = false;
      const originalSourceImage =
        originalReferencePrompt?.type === 'image' ? originalReferencePrompt.prompt?.selectedImage : null;

      submitValue.input.prompts = submitValue.input.prompts.map((prompt) => {
        if (hasAttachedSourceReference) {
          return prompt;
        }

        const shouldAttachReference =
          (isVideoType && prompt.type === 'video') ||
          (!isVideoType && prompt.type === 'image' && prompt.content === originalSourceImage);

        if (shouldAttachReference) {
          hasAttachedSourceReference = true;
          return {
            ...prompt,
            reference: {
              creation_id: sourceCreationId,
            },
          };
        }

        return prompt;
      });
    }

    const codec: NonNullable<TaskParams['settings']['codec']> = isHevcSupported() ? 'h265' : 'h264';
    const { model_version: _modelVersion, ...submitSettings } = submitValue.settings;

    return {
      ...submitValue,
      type: submitTaskType,
      settings: {
        ...submitSettings,
        ...(isVideoType ? { codec } : {}),
        use_trial: false,
      },
    } as TaskParams;
  }, [
    textPrompt,
    referencePrompts,
    validatedSettings,
    defaultFormParamsForTask,
    modelVersion,
    formTaskType,
    submitTaskType,
    settingsKey,
    isVideoType,
    originalReferencePrompt,
    sourceCreationId,
  ]);

  const isUploadingImages = useMemo(() => {
    return referencePrompts.some((prompt) => prompt.status === 'uploading');
  }, [referencePrompts]);

  const hasSourceReference = useMemo(() => {
    return referencePrompts.some((prompt) => prompt.id === originalMediaId && prompt.status === 'done');
  }, [referencePrompts, originalMediaId]);

  const clearOriginalSource = useCallback(() => {
    updateQuickEditToolDraft(mode, (draft) => ({
      ...draft,
      sourceCreationId: undefined,
      sourceVideo: null,
      sourceImage: null,
      referencePrompts: draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId),
      textPrompt: draft.textPrompt.filter((item) => !(item.type === 'mention' && item.mention.id === originalMediaId)),
    }));
  }, [mode, originalMediaId, updateQuickEditToolDraft]);

  const handleRemoveSourceMedia = useCallback(() => {
    setIsSourceMediaUploading(false);
    shouldPrependNextSourceMentionRef.current = false;
    clearOriginalSource();
    setSelectedCreationIndex(null);
  }, [clearOriginalSource]);

  const handleGetExtraItems = () => referencePrompts?.filter((it) => it.status !== 'forbidden');

  const handleGetMentionPrefix = useCallback(
    (mentionId: string, label: string): React.ReactNode | null => {
      const referencePrompt = referencePrompts.find((p) => p.id === mentionId);
      if (!referencePrompt) return null;

      let preview: string | null = null;

      if (referencePrompt.type === 'image') {
        if (referencePrompt.status === 'uploading' && !referencePrompt.prompt) {
          preview =
            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiM4MDgwODAiLz48L3N2Zz4=';
        } else {
          preview = referencePrompt.prompt?.selectedImagePreview || null;
        }
      } else if (referencePrompt.type === 'video') {
        if (mentionId === originalMediaId && currentCoverImageUrl) {
          preview = currentCoverImageUrl;
        } else {
          preview = referencePrompt.prompt?.videoPreview || null;
        }
      } else if (referencePrompt.type === 'element') {
        preview = referencePrompt.prompt?.components?.[0]?.content || null;
      }

      if (!preview) return null;

      const isVideo = referencePrompt.type === 'video';

      return (
        <div className="relative size-4 flex-shrink-0 overflow-hidden rounded-2">
          <ImageWithPlaceholder src={preview} alt={label} className="size-full object-cover" />
          {isVideo && <BaseFillPlay className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 text-xs" />}
        </div>
      );
    },
    [referencePrompts, originalMediaId, currentCoverImageUrl],
  );

  const handleMaxImageLimitClick = useCallback(() => {
    toast({
      type: 'warning',
      message: t('tools.quickEditForm.toast.maxImageError', {
        count: maxReferenceImageCount,
      }),
    });
  }, [maxReferenceImageCount, t]);

  const updateReferenceAndMentions = (targetId: string, newReference: Partial<ReferencePrompt>) => {
    updateQuickEditToolDraft(mode, (draft) => ({
      ...draft,
      referencePrompts: draft.referencePrompts.map((prompt) =>
        prompt.id === targetId ? ({ ...prompt, ...newReference } as ReferencePrompt) : prompt,
      ),
      textPrompt: draft.textPrompt.map((item) => {
        if (item.type === 'mention' && item.mention.id === targetId) {
          return {
            ...item,
            mention: {
              id: newReference.id || item.mention.id,
              name: newReference.name || item.mention.name,
              color: QUICK_EDIT_MENTION_COLOR,
            },
          };
        }

        return item;
      }),
    }));
  };

  const removeReferenceAndMentions = (idToRemove: string) => {
    updateQuickEditToolDraft(mode, (draft) => ({
      ...draft,
      referencePrompts: draft.referencePrompts.filter((prompt) => prompt.id !== idToRemove),
      textPrompt: draft.textPrompt.filter((item) => !(item.type === 'mention' && item.mention.id === idToRemove)),
    }));
  };

  const handleUploadImage = (uploadFile: ImageUploadFile) => {
    if (uploadFile.status === 'uploading') {
      updateQuickEditToolDraft(mode, (draft) => {
        const filteredPrompts = draft.referencePrompts.filter((prompt) => prompt.id !== originalMediaId);
        const nextNameIdx = getNextAvailableImageIdx(filteredPrompts, locale);
        const nextImageName = getNextImageName(nextNameIdx, locale);

        const newReference: ReferencePrompt = {
          type: 'image',
          status: 'uploading',
          prompt: uploadFile.file,
          id: uploadFile.id,
          name: nextImageName,
        } as ReferencePrompt;

        const referenceId = newReference.id;
        const referenceName = newReference.name;
        const existingMention = draft.textPrompt.find(
          (item) => item.type === 'mention' && item.mention.id === referenceId,
        );
        const contentLength = getTextPromptLength(draft.textPrompt);

        if (!existingMention && contentLength + referenceName.length + 1 >= MAX_PROMPT_LENGTH) {
          toast({
            type: 'warning',
            message: t('create.createForm.error.invalidLengthWithMention', { limit: MAX_PROMPT_LENGTH }),
          });

          return draft;
        }

        return {
          ...draft,
          referencePrompts: [...draft.referencePrompts, newReference],
          textPrompt: existingMention
            ? draft.textPrompt
            : [
                ...draft.textPrompt,
                {
                  type: 'mention',
                  mention: {
                    id: referenceId,
                    name: referenceName,
                    color: QUICK_EDIT_MENTION_COLOR,
                  },
                },
              ],
        };
      });
      return;
    }
    if (uploadFile.status === 'done') {
      updateReferenceAndMentions(uploadFile.id, {
        status: 'done',
        prompt: uploadFile.file,
      });
    } else if (uploadFile.status === 'error' || uploadFile.status === 'removed') {
      removeReferenceAndMentions(uploadFile.id);
    }
  };

  const handleBeforeDeleteMention = (mentionsIdsToDelete: string[]) => {
    const idsToRemoveFromReference: string[] = [];

    for (const id of mentionsIdsToDelete) {
      const count = textPrompt.filter((item) => item.type === 'mention' && item.mention.id === id).length;

      if (count <= 1) {
        idsToRemoveFromReference.push(id);
      }
    }

    if (idsToRemoveFromReference.length > 0) {
      if (idsToRemoveFromReference.includes(originalMediaId)) {
        clearOriginalSource();
      }

      updateQuickEditToolDraft(mode, (draft) => ({
        ...draft,
        referencePrompts: draft.referencePrompts.filter((prompt) => !idsToRemoveFromReference.includes(prompt.id)),
      }));
    }

    return true;
  };

  const handleBeforeAddMention = (mentionItem: MentionItem) => {
    const contentLength = getTextPromptLength(textPrompt);

    if (contentLength + mentionItem.label.length + 1 >= MAX_PROMPT_LENGTH) {
      toast({
        type: 'warning',
        message: t('create.createForm.error.invalidLengthWithMention', { limit: MAX_PROMPT_LENGTH }),
      });

      return false;
    }

    return true;
  };

  const toolTitle = isVideoType ? taskT('action.videoEdit') : taskT('action.imageEdit');
  const toolDescription = isVideoType
    ? taskT('action.videoQuickEditDescription')
    : taskT('action.quickEditDescription');
  const sourceVideoPrompt = useMemo<FormVideoPrompt | null>(() => {
    if (!isVideoType) return null;

    if (isSourceMediaUploading) {
      if (originalReferencePrompt?.type === 'video') {
        return {
          id: originalReferencePrompt.id,
          status: 'uploading',
          prompt: originalReferencePrompt.prompt,
        };
      }

      return {
        id: originalMediaId,
        status: 'uploading',
        prompt: null,
      };
    }

    if (originalReferencePrompt?.type !== 'video') return null;

    return {
      id: originalReferencePrompt.id,
      status: originalReferencePrompt.status,
      prompt: originalReferencePrompt.prompt,
    };
  }, [isSourceMediaUploading, isVideoType, originalMediaId, originalReferencePrompt]);
  const sourceImagePrompt = useMemo<FormImagePrompt | null>(() => {
    if (isVideoType) return null;

    if (isSourceMediaUploading) {
      if (originalReferencePrompt?.type === 'image') {
        return {
          id: originalReferencePrompt.id,
          status: 'uploading',
          prompt: originalReferencePrompt.prompt,
        };
      }

      return {
        id: originalMediaId,
        status: 'uploading',
        prompt: null,
      };
    }

    if (originalReferencePrompt?.type !== 'image') return null;

    return {
      id: originalReferencePrompt.id,
      status: originalReferencePrompt.status,
      prompt: originalReferencePrompt.prompt,
    };
  }, [isSourceMediaUploading, isVideoType, originalMediaId, originalReferencePrompt]);

  // 源图槽清空时重置路由跟踪状态
  useEffect(() => {
    if (!sourceImage && !isSourceMediaUploading) {
      isSourceClaimedRef.current = false;
      claimedForSourceSet.current.clear();
    }
  }, [sourceImage, isSourceMediaUploading]);

  const remainingVideoSlots = isVideoType ? (sourceVideoPrompt || isSourceMediaUploading ? 0 : 1) : 0;
  const dropZoneImageCount = isVideoType
    ? maxImageCount
    : maxImageCount + (!sourceImage && !isSourceMediaUploading ? 1 : 0);
  const isDropZoneDisabled = isVideoType ? remainingVideoSlots === 0 && maxImageCount === 0 : dropZoneImageCount === 0;

  return (
    <>
      <ToolFormContainer
        title={toolTitle}
        description={toolDescription}
        className="h-full overflow-y-auto pb-[72px] sm:min-h-[420px]"
        contentClassName="gap-5"
        exampleSection={<ExampleMediaPreview tool={isVideoType ? 'video_edit' : 'image_edit'} />}
        mediaSection={
          <MediaDropZone
            acceptVideo={isVideoType}
            acceptImage={true}
            maxVideoDuration={Q2_PRO_REFERENCE_VIDEO_MAX_DURATION}
            minVideoDuration={1}
            maxVideoSize={Q2_PRO_REFERENCE_VIDEO_MAX_FILE_SIZE}
            maxVideoCount={remainingVideoSlots}
            maxImageFileSize={MAX_IMAGE_FILE_SIZE}
            maxImageCount={dropZoneImageCount}
            disabled={isDropZoneDisabled}
            getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
            allowTempMode={true}
            getLimitReachedMessage={({ type }) => {
              if (type === 'image' && !isVideoType && !!sourceImagePrompt && dropZoneImageCount === 0) {
                return t('tools.quickEditForm.toast.maxTargetImageError');
              }

              return null;
            }}
            onBeforeBatchUpload={async (items) => {
              shouldPrependNextSourceMentionRef.current = false;

              const hasVideo = items.some((item) => item.type === 'video');
              const imageCount = items.filter((item) => item.type === 'image').length;

              shouldPrependNextSourceMentionRef.current = isVideoType ? hasVideo && imageCount > 0 : imageCount > 1;

              return items;
            }}
            onChange={(file, type) => {
              if (type === 'video') {
                handleUploadVideo(file as VideoUploadFile);
                return;
              }
              if (type === 'image') {
                const imageFile = file as ImageUploadFile;
                if (isVideoType) {
                  // 视频模式：图片全部作为引用图
                  handleUploadImage(imageFile);
                  return;
                }
                // 图片模式：首张路由到原图，其余路由到引用图
                // 用 fileId 追踪同一文件多次回调（uploading/done）的路由一致性
                const fileId = imageFile.id ?? '';
                if (fileId && claimedForSourceSet.current.has(fileId)) {
                  handleUploadSourceImage(imageFile);
                  return;
                }
                if (!isSourceClaimedRef.current && !sourceImage && !isSourceMediaUploading && fileId) {
                  isSourceClaimedRef.current = true;
                  claimedForSourceSet.current.add(fileId);
                  handleUploadSourceImage(imageFile);
                } else {
                  handleUploadImage(imageFile);
                }
              }
            }}
          >
            <div className={cn('relative flex w-full shrink-0 items-center justify-center')}>
              <ToolMediaUpload
                type={mode}
                className="w-full"
                header={
                  <ToolMediaUploadHeader
                    icon={
                      isVideoType ? (
                        <AgentLineGenerateVideo className="size-5 text-system-white" />
                      ) : (
                        <ProductLinePic className="size-5 text-system-white" />
                      )
                    }
                    title={isVideoType ? t('tools.targetVideo') : t('tools.targetImage')}
                  />
                }
                emptyActionsLayout="inline"
                isMobile={false}
                videoPrompt={sourceVideoPrompt}
                imagePrompt={sourceImagePrompt}
                showImageWatermark={
                  !isVideoType && !!sourceCreationId && (SITE === 'china' || userInfo?.subs_plan === 'free')
                }
                onOpenSelector={() => setOpenMediaSelector(true)}
                onRemoveMedia={handleRemoveSourceMedia}
                onVideoUpload={handleUploadVideo}
                onImageUpload={handleUploadSourceImage}
                getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                maxFileSize={isVideoType ? undefined : MAX_IMAGE_FILE_SIZE}
                maxDuration={Q2_PRO_REFERENCE_VIDEO_MAX_DURATION}
                minDuration={1}
                maxVideoSize={Q2_PRO_REFERENCE_VIDEO_MAX_FILE_SIZE}
              />
            </div>
          </MediaDropZone>
        }
        settingsSection={
          <div className="shrink-0 space-y-5">
            <div
              ref={containerRef}
              className="relative w-full rounded-[16px] border border-system-white12 bg-system-white08 p-3"
            >
              <QuickEditTextarea
                className="h-[64px]"
                textPrompt={textPrompt}
                updateTextPrompt={(nextTextPrompt) => {
                  updateQuickEditToolDraft(mode, (draft) => ({
                    ...draft,
                    textPrompt: nextTextPrompt,
                  }));
                }}
                onBeforeDeleteMention={handleBeforeDeleteMention}
                onBeforeAddMention={handleBeforeAddMention}
                getExtraItems={handleGetExtraItems}
                getPrefix={handleGetMentionPrefix}
                popupContainer={() => containerRef.current || document.body}
              />
              <div className="flex items-center justify-between sm:flex-wrap sm:gap-x-1 sm:gap-y-2">
                <div className="flex items-center gap-2 sm:gap-1">
                  <div
                    className="relative"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    {maxImageCount > 0 ? (
                      <ImageUploader
                        className="flex size-8 items-center justify-center rounded-full border border-system-white12 bg-system-white04 text-white hover:bg-system-black64"
                        maxCount={maxImageCount}
                        getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                        onChange={(file) => {
                          handleUploadImage(file);
                          markAsClicked(NewFeatureKeys.QUICK_EDIT_ADD_IMAGE);
                        }}
                        allowTempMode={true}
                      >
                        <BaseLinePlus className="size-4" />
                      </ImageUploader>
                    ) : (
                      <button
                        type="button"
                        onClick={handleMaxImageLimitClick}
                        className="flex size-8 items-center justify-center rounded-full border border-system-white12 bg-system-white04 text-white hover:bg-system-black64"
                      >
                        <BaseLinePlus className="size-4" />
                      </button>
                    )}
                    {showTooltip && (
                      <div
                        className={cn(
                          'absolute z-[51] rounded-8',
                          isQuickEditAddImageNew
                            ? '-translate-y-1/2 top-1/2 right-[52px] w-[194px] bg-system-white04'
                            : '-translate-x-1/2 -top-10 left-1/2 bg-system-bg02 px-3 py-2',
                        )}
                      >
                        {isQuickEditAddImageNew ? (
                          <>
                            <div className="relative flex h-11 w-full items-end justify-center overflow-visible">
                              <img
                                src={
                                  SITE === 'china'
                                    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/quick-edit-add-image-new-a8a257be.webp'
                                    : 'https://image01.cf.vidu.studio/vidu/media-asset/quick-edit-add-image-new-a8a257be.webp'
                                }
                                alt="add-image"
                              />
                            </div>

                            <div className="-mt-3 w-full rounded-b-8 bg-system-black/[0.01] px-3 py-2 text-center backdrop-blur-[20px]">
                              <div className="whitespace-nowrap text-sm">{t('create.createForm.upload.addImage')}</div>
                            </div>
                          </>
                        ) : (
                          <div className="whitespace-nowrap text-sm">{t('create.createForm.upload.addImage')}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <ToolSettingsWithConfig
              className="w-full"
              includedOptions={quickEditIncludedOptions}
              aspectRatioOption={{
                value: validatedSettings.aspectRatio,
                options: quickEditAspectRatioOptions,
                onChange: (value) => handleQuickEditSettingsChange({ aspectRatio: value }),
              }}
              durationOption={{
                value: validatedSettings.duration,
                options: quickEditDurationOptions,
                onChange: (value) => handleQuickEditSettingsChange({ duration: value }),
                onShouldUpgrade: () => handleOpenPlanDialog('quick-edit settings duration'),
                checkOptionValue: ({ value, subsPlan }) => !isOptionAllowed('duration', value, subsPlan, modelVersion),
              }}
              resolutionOption={{
                value: validatedSettings.resolution,
                options: quickEditResolutionOptions,
                onChange: (value) =>
                  handleQuickEditSettingsChange({ resolution: value as NonNullable<TaskSettings['resolution']> }),
              }}
            />
          </div>
        }
        submitButton={
          <ToolFormSubmitButton
            taskType={submitTaskType}
            submitValue={submitValue}
            processing={imageProcessing}
            disabled={imageProcessing || isUploadingImages || isSourceMediaUploading || !hasSourceReference}
            disabledTip={
              isUploadingImages || isSourceMediaUploading ? t('create.createForm.error.uploadingMedia') : undefined
            }
          >
            {t('create.createForm.submit')}
          </ToolFormSubmitButton>
        }
      />
      <CreationSelectorDialog
        open={openMediaSelector}
        onOpenChange={setOpenMediaSelector}
        selectedIndex={selectedCreationIndex}
        mediaType={mode}
        maxDuration={isVideoType ? Q2_PRO_REFERENCE_VIDEO_MAX_DURATION : undefined}
        minDuration={isVideoType ? 1 : undefined}
        onSelectionChange={handleSelectMedia}
        onConfirm={() => {}}
        onViewCreation={(creation) => {
          openSimpleCreationDetailModal({
            creation,
          });
        }}
      />
    </>
  );
};
